/**
 * The one thing that knows whose body a reading is (D-076(1), D-077(3), recon §B.2).
 *
 * It sits between the engine and the detectors, and everything on either side of it is deliberately
 * ignorant. The engine keeps returning frame-local poses with no notion of players. The six
 * detectors keep reading a `PoseFrame` and never learn that lanes exist — each lane's detector set
 * is simply handed a frame containing that lane's body and nothing else, which is why every Phase 3
 * invariant survives into cooperative play unchanged rather than re-implemented. The adapter keeps
 * speaking for exactly one hero. **Nothing above or below this file holds an opinion about
 * identity**, which is the D-075 lesson taken as a design input rather than found in review: only
 * one thing gets to know.
 *
 * **What it may read, exhaustively** (MIN §6, PRV-04, D-076(1)'s hard bound): the assigned lane,
 * horizontal body position, the child's own calibration, and the session's player ids. Nothing
 * facial, nothing about appearance, no embedding or descriptor of any kind — not because those
 * would work badly, but because a colour histogram of a five-year-old's jumper is a biometric
 * identifier of a child, computed on their family's laptop. `tests/architecture/lane-identity.test.ts`
 * reads this file and enforces it.
 *
 * **The four hazards the Slice 0′ spike measured, each designed for rather than discovered:**
 *
 *   - `poseIndex` is a **track slot, not an identity** (D-077(2)(a)). It survives a clean crossing
 *     and then silently renumbers across a loss. This file never reads it.
 *   - **"Two poses" is not "two children"** (D-077(2)(b)). A body half out of frame produced two
 *     readings of itself 0.01 apart; a naive lane-per-reading mechanism would have put one child's
 *     movement on both children. A separation check runs before anything is attributed.
 *   - **A full occlusion collapses into a blended reading** that is neither body and still passes
 *     the 0.6 confidence gate (D-077(2)(c)). Confidence cannot catch that; geometry must.
 *   - **Acquiring the second body costs 5–7 frames** (D-077(2)(d)). A lane that has never been
 *     acquired is *acquiring*, not *absent*, for that window — otherwise every cooperative sitting
 *     opens by declaring a child missing.
 *
 * **And the rule under all of them:** when the answer is not certain, there is no answer. Ambiguity
 * produces no attribution at all, and the adapter turns that into `recordInputUncertainty` plus
 * retractions — never an event. An emitted event carries a `playerId`, and a `playerId` is a child's
 * evidence record; there is no repairing a wrong one afterwards.
 */

import {
  COOPERATIVE_LANE_THRESHOLDS,
  type CooperativeLaneThresholds,
} from '../../platform/config/camera-lanes';
import type { GameplayTunables } from '../../shared/types/tunables';
import type { PoseCalibrationThresholds } from './pose-calibration';
import { createObservationContinuity } from './observation-continuity';
import { hipMidpoint, mirroredX, shoulderMidpoint, torsoCentre } from './pose-geometry';
import type { PoseFrame, PoseObservation } from './pose-types';

/** The two fixed lanes, in mirrored screen space: Hero 1 on the left, Hero 2 on the right (MIN §6). */
export type LaneSide = 'left' | 'right';

export const LANE_SIDES: readonly LaneSide[] = ['left', 'right'];

/**
 * Why a lane has no answer this frame. Behaviourally identical — all five mean *no attribution* —
 * and kept apart so the recovery surface Slice E′ builds can say something true about which
 * situation a family is actually in.
 */
export type LaneAmbiguityReason =
  /** The two bodies are too close together to be told apart: a crossing, or standing shoulder to shoulder. */
  | 'OVERLAP'
  /** Two readings that cannot be two children — one body read twice (D-077(2)(b)). */
  | 'DUPLICATE_READING'
  /** Two tracked bodies became one reading that is neither of them (D-077(2)(c)). */
  | 'COLLAPSED'
  /** A reading whose measurements match neither child. */
  | 'IMPLAUSIBLE_SCALE'
  /** The children appear to have ended up on each other's sides. Never silently re-attributed. */
  | 'SWAP_SUSPECTED';

export type LaneAttribution =
  /**
   * This lane's body, this frame — and whether the lane may actually be *told* about it (P-B, S4-3).
   *
   * `confirmed` is the difference between "we have decided whose body this is" and "we are still
   * deciding, and this is our best guess for the purpose of not losing track of it". An attribution
   * the resolver is still arguing with keeps its pose, because the resolver needs the position to go
   * on following the body across the frames the argument takes; what it must not do is let those
   * frames reach anything that can commit a child's evidence. Nothing downstream may read `pose`
   * without checking this — {@link attributedPose} is the one way to ask.
   */
  | { readonly kind: 'attributed'; readonly pose: PoseObservation; readonly confirmed: boolean }
  /** A second body is expected and has not arrived yet — the 5–7 frame window, not a missing child. */
  | { readonly kind: 'acquiring' }
  /** Nobody is in this lane. The shipped tracking-loss ladder takes it from here. */
  | { readonly kind: 'absent' }
  /** Somebody may be there; we cannot say it is this hero. */
  | { readonly kind: 'ambiguous'; readonly reason: LaneAmbiguityReason };

export interface LaneResolution {
  readonly left: LaneAttribution;
  readonly right: LaneAttribution;
}

export interface LaneOptions {
  /**
   * The child calibrated into this lane, when hero training has run (Slice C′).
   *
   * Only `bodyScale` is read, and only ever to **withhold** an attribution or to break a tie —
   * never to make one on its own. Two similarly-sized siblings are the common case and give it no
   * power at all, so promoting it to a primary signal would be inventing a distinction from noise.
   */
  readonly calibration?: PoseCalibrationThresholds | undefined;
}

export interface LaneResolverOptions {
  readonly left?: LaneOptions | undefined;
  readonly right?: LaneOptions | undefined;
  readonly thresholds?: CooperativeLaneThresholds | undefined;
  /**
   * The sitting's tunables, for the one number this file shares with every other layer (S6-7).
   *
   * `pose.observationGapMs` is the single definition of "the run of frames ended" (D-090(4)), and a
   * single definition is only single if everybody reads the same *copy*. This file used to capture
   * it at module load through `COOPERATIVE_LANE_THRESHOLDS.identityMemoryGapMs`, which is the
   * default and nothing else; a sitting that retunes the supported number — which is exactly what
   * the physical session exists to do — then had identity expiring on one bound while the lane that
   * inherits the body considered the same hole continuous on another.
   */
  readonly tunables?: GameplayTunables | undefined;
}

/**
 * The pose a lane may be told about, or null (P-B, S4-3).
 *
 * The only sanctioned way to read a body out of an attribution. Everything that turns a resolution
 * into something a child's detectors, measurements or evidence record will see goes through here, so
 * "a contradicted frame never reaches a semantic adapter" is one function rather than a rule each
 * consumer has to remember.
 */
export function attributedPose(attribution: LaneAttribution): PoseObservation | null {
  return attribution.kind === 'attributed' && attribution.confirmed ? attribution.pose : null;
}

/**
 * What an attribution says about whether this lane was **shown** anything (D-090(1), S6-3).
 *
 * Three answers, not two, and the distinction belongs here beside the type it reads rather than in
 * whichever consumer noticed it first. Every consumer that holds semantic state across frames has
 * to ask the same question — the pair's lane adapters, and the cooperative training screen's
 * celebration — and two copies of it is how they come to disagree about a crossing.
 *
 *  - **observed** — the lane's own body, or the honest empty picture the shipped loss ladder times.
 *    An empty lane *is* observation: the camera produced a picture and this hero was not in it.
 *  - **unobserved** — a lane the resolver is still arguing about (P-B withholds every one of those
 *    frames from it) or one it cannot read at all. Frames may be arriving at a perfectly ordinary
 *    cadence throughout; nothing about this child is being watched.
 *  - **null** — *acquiring*: neither. A lane that has never held a body has no memory an unobserved
 *    interval could falsify, and can never return to this state once it has, so treating it as a
 *    hole would only re-baseline the detectors of a child who is arriving (D-072(3)).
 */
export function laneObservation(attribution: LaneAttribution): 'observed' | 'unobserved' | null {
  switch (attribution.kind) {
    case 'absent':
      return 'observed';
    case 'attributed':
      return attribution.confirmed ? 'observed' : 'unobserved';
    case 'ambiguous':
      return 'unobserved';
    default:
      return null;
  }
}

export interface LaneResolver {
  /** Reads one frame and says what, if anything, each lane may be told. */
  resolve(frame: PoseFrame): LaneResolution;
  /** A grown-up re-trained one child (D-076(8)). The other lane is untouched. */
  setCalibration(side: LaneSide, calibration: PoseCalibrationThresholds | undefined): void;
  /**
   * The camera stopped producing pictures: forget who was standing where (S2-3).
   *
   * Everything this file knows about identity is a claim about *recent frames* — where each lane's
   * body was, which lane a lone body held, whether the pair was too close to tell apart — and a
   * stall is the end of the run of frames those claims were made about. The child who is there when
   * the pictures come back is not necessarily the child who was there when they stopped, and
   * inheriting the departed child's lane writes a sibling's every answer to their evidence record
   * with nothing on screen to say so (the D-069 family, read at this layer).
   *
   * **No longer the only thing that closes that gap, and deliberately so** (S3-2). `resolve()` now
   * expires the same memory on a gap in the frames' own timestamps, which reaches every stall
   * whether or not anything announced it — a stalled tab, a machine that fell behind, a camera
   * stopped and restarted underneath a screen that stayed subscribed. This remains for the case
   * where something *does* know: a caller with an explicit signal should not have to wait for the
   * evidence to arrive in the next frame's clock.
   *
   * Narrower than {@link reset} on purpose: the "has this lane ever held a body" flag is **not**
   * identity, and clearing it would put a recovering sitting back inside the acquisition window and
   * make the loss ladder slower to notice a child than it is for one child on their own (D-086(2)).
   * Neutral re-acquisition is about who, not about how fast.
   */
  forgetIdentity(): void;
  /** Forgets the room. Nothing carries into the next sitting. */
  reset(): void;
}

const ABSENT: LaneAttribution = { kind: 'absent' };
const ACQUIRING: LaneAttribution = { kind: 'acquiring' };

/** One body reduced to the three scalars a lane mechanism is allowed to know about it. */
interface Reading {
  readonly pose: PoseObservation;
  /** Torso centre in **mirrored** x — the side of the screen the child watches themselves on. */
  readonly centre: number;
  /** Shoulder-to-hip distance: the same measurement `PoseCalibrationThresholds.bodyScale` holds. */
  readonly scale: number;
}

function readingOf(pose: PoseObservation): Reading | null {
  const torso = torsoCentre(pose);
  const hips = hipMidpoint(pose);
  const shoulders = shoulderMidpoint(pose);
  if (torso === null || hips === null || shoulders === null) return null;

  return { pose, centre: mirroredX(torso.x), scale: Math.abs(hips.y - shoulders.y) };
}

export function createLaneResolver(options: LaneResolverOptions = {}): LaneResolver {
  const thresholds = options.thresholds ?? COOPERATIVE_LANE_THRESHOLDS;
  const calibration: Record<LaneSide, PoseCalibrationThresholds | undefined> = {
    left: options.left?.calibration,
    right: options.right?.calibration,
  };

  /** Where each lane's body was last seen, or null when this lane has nothing to continue from. */
  const lastCentre: Record<LaneSide, number | null> = { left: null, right: null };
  /** Whether a lane has ever held a body in this sitting — what turns *acquiring* into *absent*. */
  const acquired: Record<LaneSide, boolean> = { left: false, right: false };
  /** Consecutive frames a never-acquired lane has waited while somebody else was in the picture. */
  const waiting: Record<LaneSide, number> = { left: 0, right: 0 };
  /**
   * The lane a lone body belongs to, kept across the frames it is not visible in.
   *
   * This is where position gets its hysteresis (D-077(3)): a single child standing near the middle
   * whose detection drops for a frame must not come back as the other hero. Continuity covers the
   * frames they are actually in; this covers the gap.
   */
  let heldSide: LaneSide | null = null;
  /** Consecutive frames with nobody in them at all — what tells a blink from an empty room (S2). */
  let emptyFrames = 0;
  /** Whether the previous frame was ambiguous — the separation hysteresis reads it. */
  let ambiguousLast = false;
  /**
   * Whether this resolver is inside a continuous, recent run of frames (Invariant A, S3-1..S3-3).
   *
   * The one shared predicate, on the sitting's own bound (S6-7) — not a second copy of
   * `gap < 0 || gap > n` written here, which is how the two halves of a discontinuity come to
   * disagree in the first place.
   */
  const continuity = createObservationContinuity(options.tunables);
  /** When the frame being resolved was detected — the clock the cannot-read episode is timed on. */
  let lastFrameAt: number | null = null;
  /** Consecutive frames a lane's own measurements have pointed at the *other* child (Invariant B). */
  const siblingEvidenceFrames: Record<LaneSide, number> = { left: 0, right: 0 };
  /** Consecutive frames the pair's two readings have each suited the other lane's child (S4-6). */
  let pairSwapFrames = 0;
  /**
   * Whether each lane must reacquire its child neutrally before it may be told anything (S4-2).
   *
   * Set by an evidence-based refusal, which is the resolver saying out loud that it does not know
   * who this body is. What it must not become is a way of *clearing* the argument: the refusal drops
   * the memory that routed the body, and without this the very next frame would place the same body
   * in the sibling's lane by position alone and confirm it there for the rest of the mission — a
   * single step over the arithmetic midline buying a permanent re-attribution.
   *
   * Cleared only by a reacquisition that is *neutral*: the body standing decisively inside a lane's
   * own half, not straddling the line the argument was about, for as many frames as any other
   * contradiction takes to settle.
   */
  const mustReacquire: Record<LaneSide, boolean> = { left: false, right: false };
  /** Consecutive frames a lane's body has been decisively in its own half since a refusal (S4-2). */
  const reacquireFrames: Record<LaneSide, number> = { left: 0, right: 0 };
  /**
   * When the current cannot-read episode began, or null when the picture is readable (S4-4).
   *
   * Frames keep arriving throughout an ambiguity, so the stream-gap rule cannot see one: this is the
   * clock that bounds how long "we cannot read the picture" may go on preserving who was where.
   */
  let ambiguousSince: number | null = null;
  /**
   * Whether an evidence refusal happened while resolving the frame in progress (S5-2).
   *
   * Set where the refusal is made and read once the frame is finished, so arming never observes a
   * lane state that is half this frame's and half the last one's.
   */
  let refusedThisFrame = false;
  /**
   * Since when each lane has **continuously** been holding its own child (S8-1..S8-3, D-093).
   *
   * Not a flag, and that is the whole of this round: rounds 6 and 7 stored *facts about the past* —
   * "the sibling was valid when this started" — and a stored fact cannot notice that it has stopped
   * being true. It survived its lane going absent, it was read a frame late at the moment it
   * mattered, and a second refusal rewrote it with whatever the room looked like by then. Three
   * defects, one disease: the stale captured fact, which this codebase has met before and resolved
   * the same way both times (D-086, D-088) — **delete the stored fact and derive it**.
   *
   * So the lane keeps a *timestamp*, and the timestamp answers both questions at once:
   *
   *  - it is `null` the **first** frame the hold breaks — absent, unconfirmed, ambiguous, or a
   *    reading that is not decisively this lane's child. There is nothing to go stale;
   *  - re-establishment starts a **new** since and never backdates, so a body that arrives during an
   *    argument can never come to predate it;
   *  - "did this predate the argument" is then arithmetic on two timestamps rather than a boolean
   *    somebody had to remember to invalidate.
   */
  const heldContinuouslySince: Record<LaneSide, number | null> = { left: null, right: null };
  /**
   * When each lane's current doubt began, or null when it is not in doubt (S8-3).
   *
   * Captured on the **false→true transition** of `mustReacquire` and at no other time. A second
   * refusal inside an open argument restarts corroboration — it is new evidence that the lane is
   * still not identified — but it does not restart the argument, and it must not be able to hand the
   * episode a provenance that post-dates its own beginning.
   */
  const episodeArmedAt: Record<LaneSide, number | null> = { left: null, right: null };

  /** How far a reading's measurements are from a lane's child, as a fraction of that child's scale. */
  function scaleDeviation(reading: Reading, lane: LaneSide): number | null {
    const cal = calibration[lane];
    if (cal === undefined || cal.bodyScale <= 0) return null;
    return Math.abs(reading.scale - cal.bodyScale) / cal.bodyScale;
  }

  /** The veto: a reading that is not this child's size is not this child. Never the reverse. */
  function implausible(reading: Reading, lane: LaneSide): boolean {
    const deviation = scaleDeviation(reading, lane);
    return deviation !== null && deviation > thresholds.maxScaleDeviation;
  }

  function otherSide(side: LaneSide): LaneSide {
    return side === 'left' ? 'right' : 'left';
  }

  /**
   * What one reading's measurements say about which of the two children it is (Invariant B).
   *
   * The **one** piece of scale reasoning in this file, asked by the pair path and the single-body
   * path alike. Round 2 had two of them, each with its own margin invented at its own call site for
   * the same physical question, and each was wrong in its own direction: one declared a swap between
   * two children who had not moved, the other refused a child for crouching, and a third case — a
   * reading that suits *neither* child — was reported as a swap because one of two hopeless numbers
   * was smaller than the other.
   *
   * Three rules, in this order, and the order is the fix:
   *
   *   1. **Plausibility first, and separately for each child.** A comparison between two readings
   *      that are both nonsense is not evidence of anything (S3-7). "Nobody" has its own answer and
   *      it is not "swapped".
   *   2. **A margin taken from within-child variation, not from between-child difference**
   *      ({@link COOPERATIVE_LANE_THRESHOLDS.scaleVariationAllowance}). A shoulder-to-hip span is a
   *      point sample of a moving five-year-old; an advantage smaller than one child's own everyday
   *      excursion is noise, whatever it is pointing at (S3-4, S3-5).
   *   3. **It is a verdict about a reading, never about a lane.** What may be *done* with the
   *      verdict — refuse at once, or wait for it to be corroborated — belongs to the caller, which
   *      is the only thing that knows what else it has to go on.
   *
   * Where the calibrations cannot tell the two children apart this says nothing at all, and the
   * recorded lane-is-identity bound (D-079(2)) stands unchanged.
   */
  type ScaleEvidence =
    /** The profiles cannot separate these children, or one of them has no measurements yet. */
    | 'NO_EVIDENCE'
    /** Nothing in the measurements argues against this lane's child. */
    | 'THIS_LANE'
    /**
     * Decisively **this** child: credible for them, and better than the sibling by more than noise.
     *
     * The mirror image of `OTHER_LANE`, on the same margin, and it exists because identity evidence
     * has two directions and round 4 only recorded one (S5-4). Everything that acts on a *refusal*
     * treats it exactly as `THIS_LANE` — nothing here is a reason to attribute anything, and the
     * D-079(2) lane-is-identity bound is untouched. What it is allowed to do is corroborate a
     * reacquisition, where the question is not "whose lane is this" but "is this still the child the
     * argument was about".
     */
    | 'THIS_LANE_DECISIVE'
    /** Decisively the other child: credible for them, and better than this lane by more than noise. */
    | 'OTHER_LANE'
    /** Not a credible body for either child. */
    | 'NEITHER_LANE';

  function scaleEvidence(reading: Reading, side: LaneSide): ScaleEvidence {
    /**
     * The absolute veto lives **here**, and only here (S4-7).
     *
     * It used to live in {@link attribute} as well, which meant the file held two scale decisions
     * that could reach opposite verdicts about one frame — and did: for a reading implausible for
     * this lane's child but perfectly credible for their sibling, the table says `OTHER_LANE` and
     * corroborates it, while the second check refused immediately and retracted a child's answer for
     * the posture excursion S3-4 ruled must not cost them anything. A comparison that cannot be run
     * (one profile missing, or two children the calibrations cannot separate) still leaves the veto
     * standing on its own: a reading that is nobody's size is nobody's, whether or not there is
     * anybody to compare it with.
     */
    if (!distinguishable()) return implausible(reading, side) ? 'NEITHER_LANE' : 'NO_EVIDENCE';
    const other = otherSide(side);
    const mine = scaleDeviation(reading, side);
    const theirs = scaleDeviation(reading, other);
    if (mine === null || theirs === null) {
      return implausible(reading, side) ? 'NEITHER_LANE' : 'NO_EVIDENCE';
    }

    const minePlausible = mine <= thresholds.maxScaleDeviation;
    const theirsPlausible = theirs <= thresholds.maxScaleDeviation;
    if (!minePlausible && !theirsPlausible) return 'NEITHER_LANE';
    if (!minePlausible) return 'OTHER_LANE';

    /**
     * The margin, read in both directions, on **every** branch that reaches a verdict (S6-2).
     *
     * An advantage smaller than one child's own everyday scale excursion is noise whichever child it
     * happens to flatter (S3-4/S3-5, extended by S5-4). Round 5 wrote the `theirs` side of that as a
     * short-cut — *the sibling is past the absolute veto, therefore this is decisively mine* — and a
     * short-cut is what it was: a reading .251 from this child and .4008 from their sibling is not a
     * good reading of anybody, it is a less bad reading of one of them by .1498, and the whole point
     * of the allowance is that .1498 is noise. The sibling sitting a thousandth the wrong side of a
     * different threshold says nothing about that.
     *
     * So the branch is **deleted** rather than patched, and the case falls through to the one
     * comparison: a reading the sibling's profile cannot own at all still needs the margin to be
     * *decisive*, and without it the honest verdict is `THIS_LANE` — nothing here argues against
     * this lane, which is what attribution needs, and nothing here is evidence, which is what
     * clearing a refusal needs. `theirsPlausible` survives only in the `NEITHER_LANE` test above,
     * where it belongs: it is a fact about credibility, not about advantage.
     */
    if (mine - theirs > thresholds.scaleVariationAllowance) return 'OTHER_LANE';
    if (theirs - mine > thresholds.scaleVariationAllowance) return 'THIS_LANE_DECISIVE';
    return 'THIS_LANE';
  }

  /** Whether the two calibrations differ enough for scale to be allowed to say anything at all. */
  function distinguishable(): boolean {
    const left = calibration.left?.bodyScale;
    const right = calibration.right?.bodyScale;
    if (left === undefined || right === undefined || left <= 0 || right <= 0) return false;
    return Math.max(left, right) / Math.min(left, right) >= thresholds.scaleDistinguishRatio;
  }

  function forget(side: LaneSide): void {
    lastCentre[side] = null;
  }

  /**
   * Everything this resolver believes about *who*, dropped in one place (Invariant A).
   *
   * Called from three places that are all the same event wearing different clothes: a gap in the
   * frame stream, an explicit {@link LaneResolver.forgetIdentity}, and a full {@link
   * LaneResolver.reset}. `acquired` is **not** in here, deliberately and for D-086(2)'s reason: it
   * is the "has this lane ever held a body" flag, it carries no identity, and clearing it would put
   * a recovering sitting back inside the acquisition window and make a cooperative sitting slower to
   * notice a missing child than a solo one.
   */
  function expireIdentity(): void {
    lastCentre.left = null;
    lastCentre.right = null;
    waiting.left = 0;
    waiting.right = 0;
    siblingEvidenceFrames.left = 0;
    siblingEvidenceFrames.right = 0;
    pairSwapFrames = 0;
    disarm('left');
    disarm('right');
    /**
     * Nothing established before a discontinuity accounts for anybody after it (S7-1 × D-090).
     *
     * **Load-bearing, and round 7's argument that it was not has been refuted by construction**
     * (S8-5): the returning frame after a hole can itself refuse a lane, and `expireIdentity` runs
     * before that frame is resolved while the arming happens during it — so an uncleared pre-gap
     * hold was there to be read, and the refusal read it. Pinned now, not argued.
     */
    heldContinuouslySince.left = null;
    heldContinuouslySince.right = null;
    heldSide = null;
    emptyFrames = 0;
    ambiguousLast = false;
    ambiguousSince = null;
  }

  /**
   * A refusal made on **evidence** is proof the memory that routed the body was unreliable (S3-1).
   *
   * The distinction is not decoration. `OVERLAP`, `DUPLICATE_READING` and `COLLAPSED` say the
   * picture cannot be read — a lone child near the midline whose body is briefly read twice has not
   * told us anything about which lane they are in, and throwing away their lane memory over it is
   * the flicker the hysteresis exists to prevent. `SWAP_SUSPECTED` and `IMPLAUSIBLE_SCALE` say
   * something quite different: a body *was* routed somewhere by a memory, and the measurements then
   * refused it. Keeping the memory that made the mistake available for the next frame is how a
   * correct refusal is bypassed one noisy frame later.
   */
  function forgetRoutingOnRefusal(reason: LaneAmbiguityReason): void {
    if (reason !== 'SWAP_SUSPECTED' && reason !== 'IMPLAUSIBLE_SCALE') return;
    heldSide = null;
    /**
     * …and the refusal must not become the thing that *ends* the argument (S4-2).
     *
     * Dropping the memory that routed a refused body is right, and on its own it is a gadget: the
     * next frame has nothing left to argue with, so position alone places the same body — often the
     * child who simply leaned over the midline — into the sibling's lane and confirms them there.
     * Both lanes are candidates, not just the refused one, because what a refusal establishes is
     * that this resolver does not currently know which child it is looking at — and the harm is the
     * refused body claiming the *other* lane next frame.
     *
     * **Recorded, never acted on here** (S5-2). Which lanes to arm depends on which lanes end the
     * frame holding a child, and half-way through a resolution that is not yet known: round 4 read
     * `lastCentre` at this point and got whichever answer the publication order happened to leave
     * lying about. Both directions cost a child something real — a sibling whose stale centre was
     * still present was exempted and then cleared by `missing()` a few lines later, so the refused
     * body was confirmed into their lane on the next frame; and a sibling being attributed *later*
     * in the same frame had no centre yet, so they were armed and made to reacquire for a
     * contradiction that was never about them. The decision is deferred to {@link armRefusal},
     * which runs once the frame is over and reads the state the frame actually left.
     */
    refusedThisFrame = true;
  }

  /**
   * Which lanes a refusal puts in doubt, decided from the finished frame (S5-2).
   *
   * One rule, applied once, to a consistent snapshot: **a lane is exempt only if it ends this frame
   * holding a body of its own.** That is what "has continuity of its own to be argued with" actually
   * means, and it is knowable only after every lane has been published. A lane holding its own child
   * has not been put in doubt by somebody else's refusal, and arming it would hold a child's answers
   * for their sibling's camera trouble — the one thing D-076(2)(a) forbids.
   */
  function armRefusal(): void {
    if (!refusedThisFrame) return;
    for (const side of LANE_SIDES) {
      if (lastCentre[side] !== null) continue;
      arm(side);
    }
  }

  /**
   * Puts one lane in doubt, and dates the doubt — **once** (S7-1, S8-3).
   *
   * A lane already in doubt is not put into a *new* doubt by fresh evidence that it is still
   * unidentified: the corroboration counter restarts, because that evidence is real, but the episode
   * is the same episode and keeps the date it began. Round 7 rewrote the date on every refusal, so a
   * body that walked in *during* an argument could supply a provenance that appeared to predate it.
   */
  function arm(side: LaneSide): void {
    if (!mustReacquire[side]) episodeArmedAt[side] = lastFrameAt;
    mustReacquire[side] = true;
    reacquireFrames[side] = 0;
  }

  /**
   * Ends one lane's argument — flag, date and corroboration together (BL-006 S9-1).
   *
   * The counterpart {@link arm} never had. An episode used to end in three places and each of them
   * ended its own subset of it: two left the date behind, and the only reason that leaked nothing
   * was that `arm` overwrites the date on the next false→true transition. That is the stale captured
   * fact wearing its fourth disguise (D-086, D-088, S8-3) — inert today, and inert only for as long
   * as nobody reads `episodeArmedAt` one line before the flag is consulted. The three fields are one
   * fact, so they are opened in one place and closed in one place.
   */
  function disarm(side: LaneSide): void {
    mustReacquire[side] = false;
    episodeArmedAt[side] = null;
    reacquireFrames[side] = 0;
  }

  /** No attribution, and no continuity to carry into the next frame from a reading we refused. */
  function withhold(side: LaneSide, reason: LaneAmbiguityReason): LaneAttribution {
    forget(side);
    forgetRoutingOnRefusal(reason);
    return { kind: 'ambiguous', reason };
  }

  /**
   * Whether a body is standing **decisively** inside a lane's half — not leaning over the line.
   *
   * The neutrality a reacquisition after a refusal is asked for. A body sitting inside the midline
   * margin is precisely the body the argument was about, and "which half is it in" cannot settle it.
   */
  function decisivelyInHalf(centre: number, side: LaneSide): boolean {
    const margin = thresholds.midlineHysteresis;
    return side === 'left' ? centre < 0.5 - margin : centre > 0.5 + margin;
  }

  /**
   * Whether this reading corroborates the identity a refusal put in doubt (S4-2, widened by S5-4).
   *
   * **Neutrality is required where position cannot decide — not everywhere.** Round 4 asked for one
   * thing only: the body standing beyond the midline margin. That is right about the case the
   * refusal was actually about (a body straddling the line is exactly the body nobody can identify)
   * and wrong about the geometry the rest of the game is built on. A cooperative hero's three
   * portals are equal thirds of their own half (D-079(4)), so Hero 1's inner portal runs to the
   * midline itself and `midlineHysteresis` carves the last tenth of it out: a child could stand
   * where the game put them, at their own size, for ever, and never finish reacquiring — until the
   * shipped loss ladder invited them back into a picture they were standing in.
   *
   * So there are two ways to corroborate, and the second is evidence rather than a bigger margin:
   *
   *  1. **Position, where position can decide.** Decisively inside this lane's own half. Says
   *     nothing about measurements and needs none — this is the shipped rule, unchanged.
   *  2. **Measurements, where they can — and only where the sibling is somewhere else** (S6-1,
   *     narrowed by S7-1). A body **in its own half**, whose reading suits this child and is
   *     decisively not the sibling's by more than one child's own everyday scale variation, **while
   *     the other lane is already holding its own child on its own prior evidence**. That is the
   *     same single bounded scale rule the rest of this file is decided by (D-088(7)), read in the
   *     direction round 4 left unrecorded — with the conjunct round 5 left out and round 6 left
   *     circular.
   *
   * **Why the account must be *prior*, and never the disputed frame's own** (S7-1). Round 6 asked
   * only that the frame contain a second separated reading that nothing ruled out as the sibling —
   * which the disputed frame supplies for itself, and which pair geometry cannot underwrite. Two
   * separated bodies is a fact about *bodies*, not about *enrolment*: with `numPoses` capped at two,
   * the engine can perfectly well return {the child standing in the refused lane's half, a
   * grown-up}, and a parent staying nearby is an authored room condition rather than an impossible
   * input. The parent then "accounted" for the sibling's lane and the sibling was confirmed as their
   * brother. The account therefore has to come from somewhere the frame under dispute cannot
   * manufacture, and there is exactly one such place: **what the other lane had already established
   * about itself, before this frame**.
   *
   * **Why the sibling has to be accounted for, and why this is the neutrality principle rather than
   * an addition to it.** Scale is evidence about a *reading*; it was never evidence about a *room*.
   * The documented reason the allowance exists at all is that one child's apparent scale wanders by
   * more than the gap between two siblings' profiles — they crouch, lean towards the lens, stand on
   * their toes — so for a lone body "this reading suits Hero 1" and "this is Hero 2 at a flattering
   * depth" are *the same picture*, and no number of frames of it separates them. Round 5 let four
   * such frames clear a refusal. With the sibling visibly standing in their own half, the second
   * reading is what rules them out, and the first reading becomes evidence about which child this
   * is. Without it, position cannot decide and neither can anything else: there is no answer, which
   * is where this file always ends up when it does not know (D-076(1)).
   *
   * Both routes are still corroborated over `scaleCorroborationFrames`, and the accounting must hold
   * on every frame of that window — a frame in which the sibling drops out resets the counter, like
   * any other frame that fails to corroborate. Where the calibrations cannot separate the two
   * children, rule 2 says nothing at all and position remains the only route, which is the honest
   * answer for two similarly-sized siblings (D-079(2), unchanged).
   */
  function identityCorroborates(side: LaneSide, reading: Reading): boolean {
    if (decisivelyInHalf(reading.centre, side)) return true;
    // Never across the midline: the half a body is standing in is the sibling's, and no measurement
    // may hand one child's lane to a body standing in the other child's (S4-2's whole point).
    if (halfOf(reading.centre) !== side) return false;
    /**
     * …and never on this frame's own say-so, nor on anything the episode itself produced (S7-1).
     *
     * Two conditions, and they are the same condition at two moments: the other lane must have been
     * holding its own child **when this argument started**, and must still be holding it **now**.
     *
     * The first is what a third body cannot supply. A parent standing where Hero 2 stands, built
     * like Hero 2, is indistinguishable from Hero 2 by the only measurement this file has — so no
     * test of the *present* picture can tell them apart, and asking for one is asking scale to do
     * something it cannot. What a parent cannot do is have *already been* the sibling's lane's
     * confirmed child before the dispute began: they walked in afterwards, and the lane they walked
     * into was cleared by the very refusal under argument. So the account has to predate the
     * episode, and a lane that established itself inside the episode establishes nothing.
     */
    /**
     * One predicate, and it says both halves at once (S8-1..S8-3).
     *
     * The other lane must have been holding its own child **continuously since before this argument
     * began** — which is false if the hold ever broke (the since is null, or a *newer* since that
     * cannot predate the episode), false if it is broken right now, and immune to a second refusal
     * because the episode keeps its own date. There is no stored fact left to go stale, which is why
     * three separate defects close together rather than three patches.
     *
     * Read from **this frame's** holding, computed for both lanes before either clearance decision,
     * so neither publication order can see the other lane a frame late (S8-2).
     */
    const since = heldContinuouslySince[otherSide(side)];
    const armedAt = episodeArmedAt[side];
    if (since === null || armedAt === null || since >= armedAt) return false;
    return scaleEvidence(reading, side) === 'THIS_LANE_DECISIVE';
  }

  /**
   * Whether this lane may be told about the body it has been given (P-B).
   *
   * Only reached where the measurements are *not* arguing with the attribution; what is left to
   * settle is the aftermath of a refusal, which takes a corroborated reacquisition to clear.
   */
  function reacquired(side: LaneSide, reading: Reading): boolean {
    if (!mustReacquire[side]) return true;
    if (!identityCorroborates(side, reading)) {
      // The same episode, with its evidence back to nothing — which is exactly what the opener
      // does to a lane already in doubt, date and all. Restating it is not re-arming it.
      arm(side);
      return false;
    }
    reacquireFrames[side] += 1;
    if (reacquireFrames[side] <= thresholds.scaleCorroborationFrames) return false;
    disarm(side);
    return true;
  }

  function attribute(side: LaneSide, reading: Reading, confirmed: boolean): LaneAttribution {
    lastCentre[side] = reading.centre;
    acquired[side] = true;
    waiting[side] = 0;
    return { kind: 'attributed', pose: reading.pose, confirmed };
  }

  /**
   * A lane with nothing in it, while somebody else is in the picture.
   *
   * *Acquiring* until the measured second-body window has passed and then *absent*, and only for a
   * lane that has never held a body: once a lane is live, absence goes straight through so the
   * shipped `trackingLossPauseMs` ladder times it exactly as it does for a single child.
   */
  function missing(side: LaneSide): LaneAttribution {
    forget(side);
    if (acquired[side]) return ABSENT;
    waiting[side] += 1;
    return waiting[side] > thresholds.acquisitionFrames ? ABSENT : ACQUIRING;
  }

  function bothAmbiguous(reason: LaneAmbiguityReason): LaneResolution {
    // The picture cannot be read, so neither lane is holding anything this frame (S8-1).
    noHoldingThisFrame();
    forget('left');
    forget('right');
    forgetRoutingOnRefusal(reason);
    ambiguousLast = true;
    boundCannotReadEpisode(reason);
    const attribution: LaneAttribution = { kind: 'ambiguous', reason };
    return { left: attribution, right: attribution };
  }

  /**
   * How long "the picture cannot be read" may go on preserving who was standing where (S4-4).
   *
   * `OVERLAP`, `DUPLICATE_READING` and `COLLAPSED` deliberately keep a lone child's lane memory
   * (D-088(2)): a body read twice for a frame has told us nothing about which lane it is in, and
   * dropping the memory every time the detector doubles somebody is the flicker the hysteresis
   * exists to prevent. What that tolerance had was no end. Frames keep arriving at the ordinary
   * cadence throughout an ambiguity, so the frame-stream gap rule never fires, and a long unreadable
   * stretch could hide a whole change of body while keeping the departed child's lane warm for
   * whoever walked out of it.
   *
   * Past {@link COOPERATIVE_LANE_THRESHOLDS.ambiguityMemoryMs} — comfortably beyond the ~570 ms a
   * measured full-occlusion crossing lasts, so an ordinary crossing is untouched — the memory goes
   * and reacquisition is neutral. The episode's own clock is left running, so it stays expired for
   * as long as it stays unreadable rather than expiring again every frame.
   */
  function boundCannotReadEpisode(reason: LaneAmbiguityReason): void {
    if (reason === 'SWAP_SUSPECTED' || reason === 'IMPLAUSIBLE_SCALE') return;
    const at = lastFrameAt;
    if (at === null) return;
    if (ambiguousSince === null) {
      ambiguousSince = at;
      return;
    }
    if (at - ambiguousSince <= thresholds.ambiguityMemoryMs) return;
    heldSide = null;
    for (const side of LANE_SIDES) arm(side);
  }

  /** Which half of the picture a body is actually standing in. No hysteresis: this is arithmetic. */
  function halfOf(centre: number): LaneSide {
    return centre < 0.5 ? 'left' : 'right';
  }

  /** Which lane a body is standing in, when there is no continuity to carry it (with hysteresis). */
  function sideByPosition(centre: number): LaneSide {
    const margin = thresholds.midlineHysteresis;
    if (heldSide === 'left') return centre > 0.5 + margin ? 'right' : 'left';
    if (heldSide === 'right') return centre < 0.5 - margin ? 'left' : 'right';
    return centre < 0.5 ? 'left' : 'right';
  }

  /**
   * Whether the children appear to have ended up on each other's sides (D-076(1)'s hardest branch).
   *
   * Asked on **every** pair, including one positional continuity would happily carry forward (S1).
   * The old reasoning — that two bodies cannot pass through each other without their centres
   * meeting, and that centres meeting is the overlap which already produced ambiguity — is only
   * true of a crossing somebody *watched*. A crossing completed between two sampled frames leaves
   * every position exactly where it was and exchanges only the bodies, so continuity is perfect and
   * proves nothing: a clean positional match is not evidence of identity when the two children
   * could have exchanged places unseen. Where the calibrations can tell them apart they are
   * consulted first, and where they cannot this returns false and the recorded lane-is-identity
   * bound (D-079(2)) stands unchanged.
   */
  function pairSaysSwapped(lower: Reading, upper: Reading): boolean {
    return (
      scaleEvidence(lower, 'left') === 'OTHER_LANE' &&
      scaleEvidence(upper, 'right') === 'OTHER_LANE'
    );
  }

  /**
   * Whether the two children could have exchanged places since the last frame (S4-6).
   *
   * Positional history is **counter-evidence**, and the pair path had none. Two readings that each
   * suit the other child are decisive where a crossing was physically available — which is exactly
   * S1's case, two bodies a hand's breadth apart whose positions a crossing between samples would
   * leave untouched. It is not decisive where the children are half a screen apart and have not
   * moved: exchanging places would take each of them several times the furthest a body may travel in
   * one frame, and passing through each other would have produced the overlap that clears continuity
   * first. There, two individually-plausible scale readings are a depth or posture sample flattering
   * the wrong profile, and the positions say so.
   *
   * The reach is derived rather than invented: {@link
   * COOPERATIVE_LANE_THRESHOLDS.maxFrameDisplacement} is how far *one* body may move between frames,
   * and an exchange has both of them moving, so twice it is the distance an unseen crossing can
   * cover. A lane with no history to offer contradicts nothing and the evidence stands alone.
   */
  function exchangeWasReachable(lower: Reading, upper: Reading): boolean {
    const left = lastCentre.left;
    const right = lastCentre.right;
    if (left === null || right === null) return true;
    const reach = thresholds.maxFrameDisplacement * 2;
    return Math.abs(upper.centre - left) <= reach && Math.abs(lower.centre - right) <= reach;
  }

  function swapped(lower: Reading, upper: Reading): boolean {
    if (!pairSaysSwapped(lower, upper)) {
      pairSwapFrames = 0;
      return false;
    }
    pairSwapFrames += 1;
    // Corroboration is a delay, never an amnesty: a contradiction the positions argue with still
    // reaches the family if it holds, on the same counter every other contradiction is settled by.
    return (
      exchangeWasReachable(lower, upper) || pairSwapFrames > thresholds.scaleCorroborationFrames
    );
  }

  function publishPair(lower: Reading, upper: Reading): LaneResolution {
    ambiguousLast = false;
    ambiguousSince = null;
    heldSide = null;
    // The single-body corroboration counters describe a run of *lone* readings. Two bodies is a
    // different situation and ends it, whichever way the pair resolves.
    siblingEvidenceFrames.left = 0;
    siblingEvidenceFrames.right = 0;
    // P-B, on the pair path: while a swap verdict is pending corroboration the readings contradict
    // the lanes they are being put in, so the lanes keep tracking them and are told nothing.
    const contested = pairSwapFrames > 0;

    // Both lanes' holding is settled before either lane's clearance is decided, so neither
    // publication order can read the other lane a frame late or half-written (S8-2).
    const leftEvidence = scaleEvidence(lower, 'left');
    const rightEvidence = scaleEvidence(upper, 'right');
    noteHolding('left', holdingThisFrame('left', leftEvidence, contested), frameAt());
    noteHolding('right', holdingThisFrame('right', rightEvidence, contested), frameAt());

    return {
      left: publishOfPair('left', lower, leftEvidence, contested),
      right: publishOfPair('right', upper, rightEvidence, contested),
    };
  }

  function publishOfPair(
    side: LaneSide,
    reading: Reading,
    evidence: ScaleEvidence,
    contested: boolean,
  ): LaneAttribution {
    // Row one of the decision table, and the only verdict that refuses on its first frame: a reading
    // that is nobody's size is nobody's, whichever lane it turned up in.
    if (evidence === 'NEITHER_LANE') return withhold(side, 'IMPLAUSIBLE_SCALE');
    return attribute(side, reading, !contested && reacquired(side, reading));
  }

  /**
   * Whether a lane is holding its own child on **this** frame — computed for both lanes before
   * either clearance decision is taken (S8-2).
   *
   * Order-independent by construction, which is the point: the lanes are published in a fixed order,
   * so anything derived during publication is available to one of them and not the other. This is
   * derived first, from facts that do not depend on any clearance:
   *
   *  - a lane **in doubt** is not holding its own child, whatever its reading says. That is what
   *    being in doubt means, and it is also what breaks the apparent circularity — a lane cannot
   *    vouch for its sibling while its own identity is the thing under argument;
   *  - a lane with no reading, a contested pair, or a reading that is not decisively this lane's
   *    child, is not holding either.
   */
  function holdingThisFrame(
    side: LaneSide,
    evidence: ScaleEvidence | null,
    contested: boolean,
  ): boolean {
    if (mustReacquire[side]) return false;
    if (contested) return false;
    return evidence === 'THIS_LANE_DECISIVE';
  }

  /**
   * Advances the continuous-hold clock for one lane (S8-1).
   *
   * The break is the whole mechanism: the moment a lane stops holding its own child the clock is
   * `null`, and when it starts again it starts *now* — never backdated to the run it interrupted.
   * That is what makes "did this predate the argument" answerable at all.
   */
  function noteHolding(side: LaneSide, holding: boolean, at: number): void {
    if (!holding) {
      heldContinuouslySince[side] = null;
      return;
    }
    heldContinuouslySince[side] ??= at;
  }

  /** Neither lane is holding anything this frame: an unreadable picture, or an empty one. */
  function noHoldingThisFrame(): void {
    heldContinuouslySince.left = null;
    heldContinuouslySince.right = null;
  }

  /** The timestamp of the frame being resolved. */
  function frameAt(): number {
    return lastFrameAt ?? 0;
  }

  function publishSingle(
    side: LaneSide,
    reading: Reading,
    /**
     * Whether this lane has evidence of its own that this is its child: the body was matched to it
     * frame by frame **and** is standing in its half. Both halves matter — a body carried into the
     * sibling's half by continuity is exactly the arrangement S2-1 and S3-1 are about, and it is not
     * a lane defending its own child.
     */
    sameLaneEvidence: boolean,
  ): LaneResolution {
    ambiguousLast = false;
    ambiguousSince = null;
    const other = otherSide(side);

    /**
     * The decision table, applied once and in one place (S4-7, D-088(3–5)).
     *
     *   - `NEITHER_LANE` refuses immediately: nothing is being weighed, the reading is nobody's.
     *   - `OTHER_LANE` against a lane with **no** evidence of its own refuses immediately too —
     *     a body that appeared here, or is standing in the sibling's half, has nothing arguing for
     *     it (S2-1, S3-1).
     *   - `OTHER_LANE` against a lane that was matched frame to frame **and** holds its own half is
     *     the one contested row: it takes corroboration to refuse, because one frame cannot tell a
     *     posture excursion from a sibling taking over (S3-4) — and it takes P-B meanwhile, because
     *     those corroboration frames must not reach the child's detectors (S4-3).
     */
    const evidence = scaleEvidence(reading, side);
    // One reading, so the other lane is holding nobody this frame — settled before any clearance.
    noteHolding(other, false, frameAt());
    noteHolding(side, holdingThisFrame(side, evidence, false), frameAt());

    if (evidence === 'NEITHER_LANE') {
      return laneAndSibling(side, withhold(side, 'IMPLAUSIBLE_SCALE'), other);
    }

    siblingEvidenceFrames[side] = evidence === 'OTHER_LANE' ? siblingEvidenceFrames[side] + 1 : 0;

    if (evidence === 'OTHER_LANE') {
      const corroborated =
        !sameLaneEvidence || siblingEvidenceFrames[side] > thresholds.scaleCorroborationFrames;
      if (corroborated) return laneAndSibling(side, withhold(side, 'SWAP_SUSPECTED'), other);
      // Contested: tracked, so the body is not lost while this settles — and silent, so nothing it
      // does in the meantime can be written to anybody's record.
      heldSide = side;
      return laneAndSibling(side, attribute(side, reading, false), other);
    }

    // Nothing in the measurements is arguing with this lane. What may still be is the aftermath of a
    // refusal, which takes a neutral reacquisition rather than a change of position to settle.
    const mine = attribute(side, reading, reacquired(side, reading));
    heldSide = side;
    return laneAndSibling(side, mine, other);
  }

  /**
   * One lane's answer, and the empty lane beside it.
   *
   * A lane whose reading was vetoed did not hold a body, so the other lane is still only waiting —
   * and a vetoed reading does not claim a lane to come back to either: {@link withhold} has already
   * dropped the memory that routed it (S3-1).
   */
  function laneAndSibling(side: LaneSide, mine: LaneAttribution, other: LaneSide): LaneResolution {
    return side === 'left'
      ? { left: mine, right: missing(other) }
      : { left: missing(other), right: mine };
  }

  function resolvePair(first: Reading, second: Reading): LaneResolution {
    const [lower, upper] = first.centre <= second.centre ? [first, second] : [second, first];
    const separation = upper.centre - lower.centre;

    // Hazard (b): a gap this small is one body read twice, whatever the poses claim.
    if (separation < thresholds.duplicateReadingSeparation) {
      return bothAmbiguous('DUPLICATE_READING');
    }

    // Two children who are on top of each other are not two lanes. The hysteresis is what stops a
    // family standing at the threshold from flickering in and out of being readable.
    const floor = ambiguousLast
      ? thresholds.minLaneSeparation + thresholds.laneSeparationHysteresis
      : thresholds.minLaneSeparation;
    if (separation < floor) return bothAmbiguous('OVERLAP');

    /**
     * Identity evidence is consulted before anything is attributed (S1).
     *
     * Position decides *which lane* each of two separated readings is in — that much is arithmetic,
     * and it is the same answer with or without continuity, because `publishPair` assigns the lower
     * centre to the left lane either way. What position cannot decide is *whose body* a reading is,
     * and the calibrations can, so they are asked here rather than only where continuity happens to
     * have been broken. Two children who exchanged sides between sampled frames are exactly the
     * case where continuity looks strongest and is worth least.
     */
    if (swapped(lower, upper)) return bothAmbiguous('SWAP_SUSPECTED');
    return publishPair(lower, upper);
  }

  function resolveSingle(reading: Reading): LaneResolution {
    const nearLeft =
      lastCentre.left !== null &&
      Math.abs(reading.centre - lastCentre.left) <= thresholds.maxFrameDisplacement;
    const nearRight =
      lastCentre.right !== null &&
      Math.abs(reading.centre - lastCentre.right) <= thresholds.maxFrameDisplacement;

    // Hazard (c): both lanes were being tracked and one reading came back. It could be either child
    // or — as the spike measured — neither of them. A confident reading is not a confident identity.
    if (nearLeft && nearRight) return bothAmbiguous('COLLAPSED');
    const half = halfOf(reading.centre);
    if (nearLeft) return publishSingle('left', reading, half === 'left');
    if (nearRight) return publishSingle('right', reading, half === 'right');
    // Placed by position (or by the lane a lone body was holding): there is no frame-to-frame match
    // behind it, so there is nothing for scale evidence to have to argue against.
    return publishSingle(sideByPosition(reading.centre), reading, false);
  }

  /**
   * One frame, resolved — and every mutation it makes is this frame's (S5-2).
   *
   * Wrapped by {@link LaneResolver.resolve}, which brackets it with the refusal bookkeeping so that
   * a decision depending on the *whole* frame is never taken from the middle of one.
   */
  function resolveFrame(frame: PoseFrame): LaneResolution {
    /**
     * Identity memory is only valid inside a continuous, recent run of frames (Invariant A(1)).
     *
     * Asked here, before anything is resolved, because it is a fact about the *stream* rather than
     * about anything in the picture — and because the alternative, which round 2 was, is a
     * clearing call on each quiet path somebody remembered: starvation had one, the empty room had
     * one, and every review round since has found another path that did not. A stalled tab, a
     * machine that fell behind, a camera stopped and restarted underneath a screen whose
     * subscription survived it: none of them announce themselves, and all of them are the same
     * event — a hole in the frames. The frames' own timestamps see all of them at once.
     *
     * A backwards timestamp counts too: a clock that has gone the wrong way is not a run of frames
     * this resolver has been following either.
     */
    lastFrameAt = frame.detectedAt;
    /**
     * Identity only, and that is the whole of this file's business (S5-3, D-090).
     *
     * Round 4 published the discontinuity from here so that each lane's *detectors* could
     * re-baseline too, and had to bound it to lanes the resolver was attributing — which left a
     * lane that went absent one frame before a hole holding a posture nobody watched. The fact was
     * in the wrong place: what underwrites a posture claim is not this resolver's run of frames but
     * the run of frames **that lane was actually shown**, and the only thing that knows what a lane
     * was shown is that lane. Each `CameraInputAdapter` now decides it for itself from the frames it
     * is handed — which is also how a solo sitting, with no resolver at all, gets the same rule.
     *
     * The *question* is asked of the shared predicate rather than re-derived here (S6-7), so this
     * file and every lane in the sitting are reading one bound and one arithmetic.
     */
    if (continuity.note(frame.detectedAt)) expireIdentity();

    const readings = frame.poses
      .map((pose) => readingOf(pose))
      .filter((reading): reading is Reading => reading !== null);

    if (readings.length === 0) {
      // An empty picture is not a second body being acquired: there is no first body to acquire
      // one alongside, and the empty-room ladder must run on the shipped timing.
      // An empty picture: neither lane is holding anybody (S8-1).
      noHoldingThisFrame();
      forget('left');
      forget('right');
      waiting.left = 0;
      waiting.right = 0;
      siblingEvidenceFrames.left = 0;
      siblingEvidenceFrames.right = 0;
      pairSwapFrames = 0;
      ambiguousLast = false;
      ambiguousSince = null;
      /**
       * What a full loss must and must not carry, decided one field at a time (S2).
       *
       * - `lastCentre` — cleared, above. Continuity is a claim about consecutive frames and there
       *   were none.
       * - `waiting` — reset, above. The acquisition window is about a *second* body arriving
       *   beside a first one, and there is no first one.
       * - `ambiguousLast` — reset, above. The separation hysteresis describes a pair the camera
       *   is currently looking at.
       * - `heldSide` — **cleared here, after a short blink tolerance.** It is memory of *whose*
       *   lane a lone body was in, and after an empty room the next body to appear is not
       *   necessarily that child. This is the one field whose survival could misattribute a
       *   child's actions to their sibling, which is why it is the one that goes.
       * - `acquired` — **deliberately kept.** It carries no identity: it is the "has this lane
       *   ever held a body" flag that decides whether an empty lane is *acquiring* or *absent*,
       *   and clearing it would put a re-acquiring sitting back inside the seven-frame window and
       *   make the shipped loss ladder slower to notice a child than it is for one child on their
       *   own (D-079(3)). Neutral re-acquisition is about who, not about how fast.
       */
      emptyFrames += 1;
      if (emptyFrames > thresholds.midlineMemoryFrames) {
        heldSide = null;
        // A room that emptied gives whoever walks back in a neutral, positional re-acquisition by
        // construction, so there is no argument left over for one to have to settle (S4-2).
        disarm('left');
        disarm('right');
      }
      return { left: ABSENT, right: ABSENT };
    }
    emptyFrames = 0;

    /**
     * The two readings the engine was surest about, when it returned more than two.
     *
     * `numPoses` caps the real thing at two, so this is a guard rather than a policy — and it
     * orders by confidence rather than by array position, because the array position *is* the
     * track slot and the slot is not an identity (D-077(2)(a)).
     */
    const [first, second] = [...readings].sort(
      (a, b) => b.pose.coreConfidence - a.pose.coreConfidence,
    );
    if (first === undefined) return { left: ABSENT, right: ABSENT };
    if (second === undefined) return resolveSingle(first);
    return resolvePair(first, second);
  }

  return {
    resolve(frame: PoseFrame): LaneResolution {
      refusedThisFrame = false;
      const resolution = resolveFrame(frame);
      // Decided last, from the state the frame actually left behind: see `armRefusal`.
      armRefusal();
      return resolution;
    },

    setCalibration(side: LaneSide, next: PoseCalibrationThresholds | undefined): void {
      calibration[side] = next;
    },

    forgetIdentity(): void {
      // Exactly the empty-frame branch's fields, and for exactly its reasons — the difference is
      // that a stall is not a blink, so there is no tolerance in front of it: the empty-room ladder
      // waits a frame or two to tell a dropped detection from a room somebody left, and a camera
      // that has stopped producing pictures at all has already told us which of the two this is.
      expireIdentity();
      // The run of frames is over, so the next frame starts one rather than continuing this one.
      continuity.forget();
      lastFrameAt = null;
    },

    reset(): void {
      expireIdentity();
      continuity.forget();
      lastFrameAt = null;
      acquired.left = false;
      acquired.right = false;
    },
  };
}
